import { useEffect } from "react";

export const SITE_URL = "https://instaportdelivery.com";
export const SITE_NAME = "Instaport Delivery";
export const DEFAULT_IMAGE = `${SITE_URL}/assets/hero-img2.png`;
export const LOGO_URL = `${SITE_URL}/assets/logo/logo.png`;
export const CONTACT_PHONE = "+91 88501 66601";
export const CONTACT_EMAIL = "support@instaportdelivery.com";
export const CONTACT_ADDRESS =
  "R/3 A Wing ground floor Mass Metropolis Chembur Mumbai Maharashtra 400071";

const defaultDescription =
  "Instaport Delivery offers fast, secure and affordable courier, parcel, bulk order and business delivery services in Mumbai. Book pickup and drop delivery online.";

const defaultKeywords = [
  "Instaport",
  "Instaport Delivery",
  "Instaport customer care",
  "courier delivery service",
  "delivery service Mumbai",
  "parcel delivery Mumbai",
  "same day delivery Mumbai",
  "local delivery service",
  "business delivery service",
  "bulk courier delivery",
  "pickup and drop service",
];

const setMeta = (selector, attrName, attrValue, content) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attrName, attrValue);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

export const absoluteUrl = (path = "/") => {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

export const organizationSchema = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: ["Instaport", "Instaport Delivery"],
  url: SITE_URL,
  logo: LOGO_URL,
  email: CONTACT_EMAIL,
  telephone: CONTACT_PHONE,
  sameAs: [
    "https://www.facebook.com/people/instaportdelivery/61590141218960/",
    "https://www.instagram.com/instaportdelivery",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: CONTACT_PHONE,
      contactType: "customer care",
      areaServed: "IN",
      availableLanguage: ["en", "hi"],
    },
  ],
};

export const localBusinessSchema = {
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#local-business`,
  name: SITE_NAME,
  image: DEFAULT_IMAGE,
  url: SITE_URL,
  telephone: CONTACT_PHONE,
  email: CONTACT_EMAIL,
  priceRange: "INR",
  address: {
    "@type": "PostalAddress",
    streetAddress: "R/3 A Wing ground floor Mass Metropolis, Chembur",
    addressLocality: "Mumbai",
    addressRegion: "Maharashtra",
    postalCode: "400071",
    addressCountry: "IN",
  },
  areaServed: [
    {
      "@type": "City",
      name: "Mumbai",
    },
  ],
  sameAs: organizationSchema.sameAs,
};

export const websiteSchema = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  alternateName: "Instaport",
  publisher: {
    "@id": `${SITE_URL}/#organization`,
  },
};

export const serviceSchema = {
  "@type": "Service",
  "@id": `${SITE_URL}/#delivery-service`,
  name: "Courier and parcel delivery service",
  provider: {
    "@id": `${SITE_URL}/#organization`,
  },
  areaServed: "Mumbai, Maharashtra, India",
  serviceType: [
    "Courier delivery",
    "Parcel delivery",
    "Same-day delivery",
    "Bulk order delivery",
    "Business delivery",
    "Pickup and drop service",
  ],
  description:
    "Fast courier, parcel, same-day, bulk order and business delivery services for customers and companies in Mumbai.",
  url: SITE_URL,
};

export const webPageSchema = ({ title, description, path = "/" }) => ({
  "@type": "WebPage",
  "@id": `${absoluteUrl(path)}#webpage`,
  url: absoluteUrl(path),
  name: title,
  description,
  isPartOf: {
    "@id": `${SITE_URL}/#website`,
  },
  about: {
    "@id": `${SITE_URL}/#organization`,
  },
});

export const breadcrumbSchema = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export const faqSchema = (items) => ({
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

export const useSEO = ({
  title,
  description = defaultDescription,
  path = "/",
  keywords = [],
  image = DEFAULT_IMAGE,
  type = "website",
  noIndex = false,
  structuredData = [],
}) => {
  useEffect(() => {
    const pageTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const pageUrl = absoluteUrl(path);
    const pageImage = absoluteUrl(image);
    const robots = noIndex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
    const allKeywords = [...defaultKeywords, ...keywords].join(", ");

    document.title = pageTitle;
    document.documentElement.setAttribute("lang", "en-IN");

    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[name="keywords"]', "name", "keywords", allKeywords);
    setMeta('meta[name="robots"]', "name", "robots", robots);
    setMeta('meta[name="googlebot"]', "name", "googlebot", robots);
    setMeta('meta[name="author"]', "name", "author", SITE_NAME);
    setMeta('meta[name="application-name"]', "name", "application-name", SITE_NAME);

    setMeta('meta[property="og:title"]', "property", "og:title", pageTitle);
    setMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description
    );
    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:url"]', "property", "og:url", pageUrl);
    setMeta('meta[property="og:image"]', "property", "og:image", pageImage);
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_NAME);
    setMeta('meta[property="og:locale"]', "property", "og:locale", "en_IN");

    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", pageTitle);
    setMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description
    );
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", pageImage);

    setLink("canonical", pageUrl);

    const graph = [
      organizationSchema,
      localBusinessSchema,
      websiteSchema,
      ...structuredData,
    ];

    let script = document.head.querySelector("#instaport-structured-data");
    if (!script) {
      script = document.createElement("script");
      script.id = "instaport-structured-data";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": graph,
    });
  }, [description, image, keywords, noIndex, path, structuredData, title, type]);
};
